/**
 * Workshop Management Page
 * Admin page for managing workshops and user assignments
 */

import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  Building2,
  Plus,
  Edit,
  Trash2,
  Users,
  Database,
  MapPin,
  Check,
  X,
} from "lucide-react";
import { Workshop } from "../../types/workshop";
import workshopService from "../../services/workshopService";
import { useSnackbar } from "notistack";
import { useTheme } from "../../contexts/ThemeContext";
import UserWorkshopAssignmentModal from "./UserWorkshopAssignmentModal";
import WorkshopFormModal from "./WorkshopFormModal";
import api from "../../services/api";

const WorkshopManagement: React.FC = () => {
  const [workshops, setWorkshops] = useState<Workshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingWorkshop, setEditingWorkshop] = useState<Workshop | null>(null);
  const [selectedWorkshop, setSelectedWorkshop] = useState<Workshop | null>(
    null
  );
  const [showUserAssignment, setShowUserAssignment] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    loadWorkshops();
    loadTotalUsers();
  }, []);

  const loadWorkshops = async () => {
    try {
      setLoading(true);
      const data = await workshopService.getWorkshops();
      setWorkshops(data);
    } catch (error) {
      enqueueSnackbar("Error loading workshops", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  const loadTotalUsers = async () => {
    try {
      const allUsers = await api.get("/users/");
      // Count only non-admin users
      const nonAdminUsers = Array.isArray(allUsers)
        ? allUsers.filter((user: any) => !user.is_admin)
        : [];
      setTotalUsers(nonAdminUsers.length);
    } catch (error) {
      console.error("Error loading users count:", error);
    }
  };

  const handleManageUsers = (workshop: Workshop) => {
    setSelectedWorkshop(workshop);
    setShowUserAssignment(true);
  };

  const handleUserAssignmentSave = () => {
    loadWorkshops();
  };

  const handleCreateWorkshop = () => {
    setEditingWorkshop(null);
    setShowFormModal(true);
  };

  const handleEditWorkshop = (workshop: Workshop) => {
    setEditingWorkshop(workshop);
    setShowFormModal(true);
  };

  const handleDeleteWorkshop = async (workshop: Workshop) => {
    if (
      !window.confirm(
        `Are you sure you want to delete "${workshop.name}"? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      await api.delete(`/workshops/${workshop.id}`);
      enqueueSnackbar("Workshop deleted successfully", { variant: "success" });
      loadWorkshops();
    } catch (error: any) {
      const message = error.response?.data?.detail || "Error deleting workshop";
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const headerTitleClass = `text-3xl font-bold ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const headerSubtitleClass = `mt-1 ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;
  const loadingSpinnerClass = `animate-spin rounded-full h-12 w-12 border-b-2 ${
    isDarkMode ? "border-blue-400" : "border-blue-600"
  }`;

  const statCardBase = "rounded-xl p-6 border shadow-sm";
  const totalWorkshopsCardClass = `${statCardBase} ${
    isDarkMode
      ? "bg-gradient-to-br from-blue-900/30 to-blue-800/30 border-blue-700/50"
      : "bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200"
  }`;
  const totalWorkshopsLabelClass = `text-sm font-medium ${
    isDarkMode ? "text-blue-300" : "text-blue-700"
  }`;
  const totalWorkshopsNumberClass = `text-3xl font-bold ${
    isDarkMode ? "text-blue-100" : "text-blue-900"
  }`;
  const totalWorkshopsIconClass = `w-12 h-12 ${
    isDarkMode ? "text-blue-400 opacity-50" : "text-blue-600 opacity-40"
  }`;

  const activeWorkshopsCardClass = `${statCardBase} ${
    isDarkMode
      ? "bg-gradient-to-br from-green-900/30 to-green-800/30 border-green-700/50"
      : "bg-gradient-to-br from-green-50 to-green-100 border-green-200"
  }`;
  const activeWorkshopsLabelClass = `text-sm font-medium ${
    isDarkMode ? "text-green-300" : "text-green-700"
  }`;
  const activeWorkshopsNumberClass = `text-3xl font-bold ${
    isDarkMode ? "text-green-100" : "text-green-900"
  }`;
  const activeWorkshopsIconClass = `w-12 h-12 ${
    isDarkMode ? "text-green-400 opacity-50" : "text-green-600 opacity-40"
  }`;

  const totalUsersCardClass = `${statCardBase} ${
    isDarkMode
      ? "bg-gradient-to-br from-purple-900/30 to-purple-800/30 border-purple-700/50"
      : "bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200"
  }`;
  const totalUsersLabelClass = `text-sm font-medium ${
    isDarkMode ? "text-purple-300" : "text-purple-700"
  }`;
  const totalUsersNumberClass = `text-3xl font-bold ${
    isDarkMode ? "text-purple-100" : "text-purple-900"
  }`;
  const totalUsersIconClass = `w-12 h-12 ${
    isDarkMode ? "text-purple-400 opacity-50" : "text-purple-600 opacity-40"
  }`;

  const emptyStateContainerClass = `text-center py-12 rounded-xl border-2 border-dashed ${
    isDarkMode ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-300"
  }`;
  const emptyStateIconClass = `w-16 h-16 mx-auto mb-4 ${
    isDarkMode ? "text-gray-500" : "text-gray-400"
  }`;
  const emptyStateTitleClass = `text-lg font-semibold mb-2 ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const emptyStateTextClass = `mb-4 ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className={loadingSpinnerClass}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <p className={headerSubtitleClass}>
            Manage workshops, databases, and user assignments
          </p>
        </div>
        <button
          onClick={handleCreateWorkshop}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                   hover:bg-blue-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          New Workshop
        </button>
      </div>
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className={totalWorkshopsCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={totalWorkshopsLabelClass}>Total Workshops</p>
              <p className={`${totalWorkshopsNumberClass} mt-1`}>
                {workshops.length}
              </p>
            </div>
            <Building2 className={totalWorkshopsIconClass} />
          </div>
        </div>

        <div className={activeWorkshopsCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={activeWorkshopsLabelClass}>Active Workshops</p>
              <p className={`${activeWorkshopsNumberClass} mt-1`}>
                {workshops.filter((w) => w.is_active).length}
              </p>
            </div>
            <Check className={activeWorkshopsIconClass} />
          </div>
        </div>

        <div className={totalUsersCardClass}>
          <div className="flex items-center justify-between">
            <div>
              <p className={totalUsersLabelClass}>Total Users</p>
              <p className={`${totalUsersNumberClass} mt-1`}>{totalUsers}</p>
            </div>
            <Users className={totalUsersIconClass} />
          </div>
        </div>
      </div>
      {/* Workshops Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {workshops.map((workshop) => (
          <WorkshopCard
            key={workshop.id}
            workshop={workshop}
            onManageUsers={handleManageUsers}
            onEdit={handleEditWorkshop}
            onDelete={handleDeleteWorkshop}
            isDarkMode={isDarkMode}
          />
        ))}
      </div>{" "}
      {workshops.length === 0 && (
        <div className={emptyStateContainerClass}>
          <Building2 className={emptyStateIconClass} />
          <h3 className={emptyStateTitleClass}>No Workshops Yet</h3>
          <p className={emptyStateTextClass}>
            Create your first workshop to get started
          </p>
          <button
            onClick={handleCreateWorkshop}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg
                     hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            Create Workshop
          </button>
        </div>
      )}
      {/* Workshop Form Modal */}
      <WorkshopFormModal
        workshop={editingWorkshop}
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSave={loadWorkshops}
      />
      {/* User Assignment Modal */}
      {selectedWorkshop && (
        <UserWorkshopAssignmentModal
          workshop={selectedWorkshop}
          isOpen={showUserAssignment}
          onClose={() => setShowUserAssignment(false)}
          onSave={handleUserAssignmentSave}
        />
      )}
    </div>
  );
};

interface WorkshopCardProps {
  workshop: Workshop;
  onManageUsers: (workshop: Workshop) => void;
  onEdit: (workshop: Workshop) => void;
  onDelete: (workshop: Workshop) => void;
  isDarkMode: boolean;
}

const WorkshopCard: React.FC<WorkshopCardProps> = ({
  workshop,
  onManageUsers,
  onEdit,
  onDelete,
  isDarkMode,
}) => {
  const [carCount, setCarCount] = useState<number | null>(null);
  const [loadingCount, setLoadingCount] = useState(false);

  useEffect(() => {
    loadCarCount();
  }, [workshop.id]);

  const loadCarCount = async () => {
    try {
      setLoadingCount(true);
      const result = await workshopService.countCars(workshop.id);
      setCarCount(result.total_cars);
    } catch (error) {
      console.error("Error loading car count:", error);
    } finally {
      setLoadingCount(false);
    }
  };

  const cardClass = `rounded-xl border shadow-sm hover:shadow-md transition-shadow overflow-hidden ${
    isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
  }`;
  const headerClass = `p-6 border-b ${
    isDarkMode ? "border-gray-700" : "border-gray-100"
  }`;
  const iconWrapperClass = `p-3 rounded-lg border ${
    isDarkMode
      ? "bg-blue-900/30 border-blue-800/50"
      : "bg-blue-50 border-blue-100"
  }`;
  const iconClass = `w-6 h-6 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`;
  const titleClass = `text-xl font-bold ${
    isDarkMode ? "text-white" : "text-gray-900"
  }`;
  const locationIconClass = `w-4 h-4 ${
    isDarkMode ? "text-gray-500" : "text-gray-400"
  }`;
  const locationTextClass = `text-sm ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;
  const badgeActiveClass = `inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
    isDarkMode
      ? "bg-green-900/30 text-green-400"
      : "bg-green-100 text-green-700"
  }`;
  const badgeInactiveClass = `inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
    isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"
  }`;
  const contentLabelClass = `text-sm font-medium ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;
  const codeBadgeClass = `px-3 py-1 rounded-md text-sm font-mono border ${
    isDarkMode
      ? "bg-gray-700/70 text-gray-100 border-gray-600/50"
      : "bg-gray-100 text-gray-900 border-gray-200"
  }`;
  const databaseIconClass = `w-4 h-4 ${
    isDarkMode ? "text-gray-400" : "text-gray-500"
  }`;
  const databaseCodeClass = `text-sm font-mono ${
    isDarkMode ? "text-gray-100" : "text-gray-900"
  }`;
  const vehiclesSectionClass = `flex items-center justify-between pt-4 border-t ${
    isDarkMode ? "border-gray-700" : "border-gray-100"
  }`;
  const vehicleSkeletonClass = `animate-pulse h-6 w-16 rounded ${
    isDarkMode ? "bg-gray-700" : "bg-gray-200"
  }`;
  const vehicleCountClass = `text-2xl font-bold ${
    isDarkMode ? "text-blue-400" : "text-blue-600"
  }`;
  const descriptionSectionClass = `pt-4 border-t ${
    isDarkMode ? "border-gray-700" : "border-gray-100"
  }`;
  const descriptionTextClass = `text-sm ${
    isDarkMode ? "text-gray-400" : "text-gray-600"
  }`;
  const footerClass = `p-4 flex gap-2 border-t ${
    isDarkMode ? "bg-gray-900/50 border-gray-700" : "bg-gray-50 border-gray-100"
  }`;
  const manageButtonClass = `flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium transition-all border ${
    isDarkMode
      ? "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-gray-500"
      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400"
  }`;
  const editButtonClass = `flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all border ${
    isDarkMode
      ? "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-blue-500 hover:text-blue-400"
      : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-blue-400 hover:text-blue-600"
  }`;
  const deleteButtonClass = `flex items-center justify-center gap-2 px-4 py-2 rounded-lg transition-all border ${
    isDarkMode
      ? "bg-gray-800 border-red-700/70 text-red-400 hover:bg-red-900/30 hover:border-red-600"
      : "bg-white border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
  }`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={cardClass}
    >
      <div className={headerClass}>
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className={iconWrapperClass}>
              <Building2 className={iconClass} />
            </div>
            <div>
              <h3 className={titleClass}>{workshop.name}</h3>
              <div className="flex items-center gap-2 mt-1">
                <MapPin className={locationIconClass} />
                <span className={locationTextClass}>{workshop.location}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {workshop.is_active ? (
              <span className={badgeActiveClass}>
                <Check className="w-3 h-3" />
                Active
              </span>
            ) : (
              <span className={badgeInactiveClass}>
                <X className="w-3 h-3" />
                Inactive
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className={contentLabelClass}>Code:</span>
          <code className={codeBadgeClass}>{workshop.code}</code>
        </div>

        <div className="flex items-start justify-between">
          <span className={contentLabelClass}>Database:</span>
          <div className="flex items-center gap-2">
            <Database className={databaseIconClass} />
            <code className={databaseCodeClass}>{workshop.db_name}</code>
          </div>
        </div>

        <div className={vehiclesSectionClass}>
          <span className={contentLabelClass}>Total Vehicles:</span>
          {loadingCount ? (
            <div className={vehicleSkeletonClass}></div>
          ) : (
            <span className={vehicleCountClass}>
              {carCount !== null ? carCount : "-"}
            </span>
          )}
        </div>

        {workshop.description && (
          <div className={descriptionSectionClass}>
            <p className={descriptionTextClass}>{workshop.description}</p>
          </div>
        )}
      </div>

      <div className={footerClass}>
        <button
          onClick={() => onManageUsers(workshop)}
          className={manageButtonClass}
        >
          <Users className="w-4 h-4" />
          Manage Users
        </button>
        <button onClick={() => onEdit(workshop)} className={editButtonClass}>
          <Edit className="w-4 h-4" />
        </button>
        <button
          onClick={() => onDelete(workshop)}
          className={deleteButtonClass}
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </motion.div>
  );
};

export default WorkshopManagement;
